import { Form, Input, Select, Cascader, Button, Collapse, Divider, Badge, Upload } from 'antd';
import { useState, useEffect } from 'react';
import { DownOutlined, BankOutlined, GlobalOutlined, TeamOutlined, ToolOutlined, DollarOutlined, UploadOutlined, FundOutlined, CalendarOutlined } from '@ant-design/icons';
import companyOptions from '../data/companyOptions.json';
import regionCountryOptions from '../data/regionCountryOptions.json';
import employeeCountOptions from '../data/employeeCountOptions.json';
import industryOptions from '../data/industryOptions.json';
import technologyOptions from '../data/technologyOptions.json';
import revenueOptions from '../data/revenueOptions.json';
import companyKeywordOptions from '../data/companyKeywords.json';
import fundingOptions from '../data/fundingOptions.json';
import Papa from 'papaparse';

const { Option } = Select;
const { Panel } = Collapse;

export default function CompanyFilter({ form, handleFilter, handleReset, initialValues }) {
  // UI toggles
  const [showExcludeCompany, setShowExcludeCompany] = useState(false);
  const [showExcludeCompanyLocation, setShowExcludeCompanyLocation] = useState(false);
  const [showCustomEmployeeCount, setShowCustomEmployeeCount] = useState(false);
  const [minEmployeeCount, setMinEmployeeCount] = useState(null);
  const [maxEmployeeCount, setMaxEmployeeCount] = useState(null);
  const [showCustomRevenue, setShowCustomRevenue] = useState(false);
  const [minRevenue, setMinRevenue] = useState(null);
  const [maxRevenue, setMaxRevenue] = useState(null);
  const [showCustomFoundingYear, setShowCustomFoundingYear] = useState(false);
  const [minFoundingYear, setMinFoundingYear] = useState(null);
  const [maxFoundingYear, setMaxFoundingYear] = useState(null);
  const [showExcludeIndustry, setShowExcludeIndustry] = useState(false);

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [initialValues, form]);

  // AntD 4.23+ live hooks for badges
  const industry = Form.useWatch('industryOptions', form) || [];
  const excludeIndustry = Form.useWatch('excludeIndustryOptions', form) || [];
  const employeeCount = Form.useWatch('employeeCountOptions', form) || [];
  const customEmployeeCount = showCustomEmployeeCount && minEmployeeCount && maxEmployeeCount ? [`${minEmployeeCount}-${maxEmployeeCount}`] : [];
  const revenue = Form.useWatch('revenueOptions', form) || [];
  const customRevenueArr = showCustomRevenue && minRevenue && maxRevenue ? [`${minRevenue}-${maxRevenue}`] : [];
  const foundingYear = Form.useWatch('foundingYearOptions', form) || [];
  const customFoundingYearArr = showCustomFoundingYear && minFoundingYear && maxFoundingYear ? [`${minFoundingYear}-${maxFoundingYear}`] : [];
  const companyLocation = Form.useWatch('companyLocationOptions', form) || [];
  const excludeCompanyLocation = Form.useWatch('excludeCompanyLocationOptions', form) || [];
  const technology = Form.useWatch('technologyOptions', form) || [];
  const includeCompanyOptions = Form.useWatch('includeCompanyOptions', form) || [];
  const excludeCompanyOptions = Form.useWatch('excludeCompanyOptions', form) || [];
  const companyKeywords = Form.useWatch('companyKeywordOptions', form) || [];
  const funding = Form.useWatch('fundingOptions', form) || [];

  // Helper for custom input toggles: badge shows custom value count if present
  const badgeCount = (arr, customArr) => (customArr && customArr.length > 0 ? customArr.length : (Array.isArray(arr) ? arr.length : 0));

  const toggleCustomEmployeeCount = () => setShowCustomEmployeeCount(e => !e);
  const toggleCustomRevenue = () => setShowCustomRevenue(e => !e);
  const toggleCustomFoundingYear = () => setShowCustomFoundingYear(e => !e);

  const handleMinEmployeeCountChange = (e) => setMinEmployeeCount(e.target.value);
  const handleMaxEmployeeCountChange = (e) => setMaxEmployeeCount(e.target.value);
  const handleMinRevenueChange = (e) => setMinRevenue(e.target.value);
  const handleMaxRevenueChange = (e) => setMaxRevenue(e.target.value);
  const handleMinFoundingYearChange = (e) => setMinFoundingYear(e.target.value);
  const handleMaxFoundingYearChange = (e) => setMaxFoundingYear(e.target.value);

  const handleCSVUpload = (file, fieldName) => {
    Papa.parse(file, {
      complete: (result) => {
        const formattedCompanies = result.data.flat().map((company) => company.trim()).filter((company) => company.length > 0);
        const currentValues = form.getFieldValue(fieldName) || [];
        form.setFieldsValue({
          [fieldName]: [...new Set([...currentValues, ...formattedCompanies])],
        });
      },
    });
    return false;
  };

  const onFinish = (values) => {
    const countryValues = values.companyLocationOptions || [];
    const expandedCountryValues = countryValues.flatMap(parent => {
      if (parent.length === 1) {
        const parentOption = regionCountryOptions.find(option => option.value === parent[0]);
        return parentOption ? parentOption.children.map(child => child.value) : [parent[0]];
      } else if (parent.length === 2) {
        return parent[1];
      }
      return parent;
    });

    const excludeCountryValues = values.excludeCompanyLocationOptions || [];
    const expandedExcludeCountryValues = excludeCountryValues.flatMap(parent => {
      if (parent.length === 1) {
        const parentOption = regionCountryOptions.find(option => option.value === parent[0]);
        return parentOption ? parentOption.children.map(child => child.value) : [parent[0]];
      } else if (parent.length === 2) {
        return parent[1];
      }
      return parent;
    });

    const finalCountryValues =  expandedCountryValues.filter(country => !expandedExcludeCountryValues.includes(country));
    const technologyValues = values.technologyOptions || [];
    const expandedTechnologyValues = technologyValues.flatMap(parent => {
      if (parent.length === 1) {
        const parentOption = technologyOptions.find(option => option.value === parent[0]);
        return parentOption ? parentOption.children.map(child => child.value) : [parent[0]];
      } else if (parent.length === 2) {
        return parent[1];
      }
      return parent;
    });

    const employeeCount_company = showCustomEmployeeCount && minEmployeeCount && maxEmployeeCount
      ? [`${minEmployeeCount}-${maxEmployeeCount}`]
      : values.employeeCountOptions || [];

    const companyRevenue_company = showCustomRevenue && minRevenue && maxRevenue
      ? [`${minRevenue}-${maxRevenue}`]
      : values.revenueOptions || [];

    const foundingYear_company = showCustomFoundingYear && minFoundingYear && maxFoundingYear
      ? [`${minFoundingYear}-${maxFoundingYear}`]
      : values.foundingYearOptions || [];

    const updatedValues = {
      ...values,
      employeeCount_company,
      companyRevenue_company,
      foundingYear_company,
      includeCompanyLocation_company:finalCountryValues,
      excludeCompanyLocation_company:expandedExcludeCountryValues,
      includeIndustry_company:values.industryOptions || [],
      excludeIndustry_company: values.excludeIndustryOptions || [],
      includeComapny_company:values.includeCompanyOptions || [],
      excludeCompany_company:values.excludeCompanyOptions || [],
      includeTechnology_company: expandedTechnologyValues,
      includeCompanyKeyword_company: values.companyKeywordOptions || [],
      funding_company: values.fundingOptions || []
    };

    handleFilter(updatedValues);
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <div style={{ height: 'calc(125vh - 300px)', padding: 24, overflowY: 'auto' }}>
        <Form.Item name="search_company" label="Search in companies">
          <Input placeholder="Enter Search for company name" />
        </Form.Item>
        <Collapse className='filter-tabs' style={{marginBottom:20}} expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />} >
          <Panel header="Industry" key="0"
                 extra={<Badge count={industry.length} offset={[10, 0]}><BankOutlined /></Badge>}>
            <Form.Item name="industryOptions" label="Industry" style={{marginBottom:5}}>
              <Select
                placeholder="Select Industry"
                mode="multiple"
                showSearch
                optionFilterProp="children"
              >
                {industryOptions[0].options.map(opt => (
                  <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                ))}
              </Select>
            </Form.Item>
            <Button type="link" style={{ padding: 0, marginBottom: 5 }} onClick={() => setShowExcludeIndustry(e => !e)}>
              {showExcludeIndustry ? 'Hide Exclude Industry' : 'Show Exclude Industry'}
            </Button>
            {showExcludeIndustry && (
              <Form.Item name="excludeIndustryOptions" label="Exclude Industry">
                <Select
                  placeholder="Select Industry to Exclude"
                  mode="multiple"
                  showSearch
                  optionFilterProp="children"
                >
                  {industryOptions[0].options.map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </Panel>
          <Panel header="Employee Count" key="1"
                 extra={<Badge count={badgeCount(employeeCount, customEmployeeCount)} offset={[10, 0]}><TeamOutlined /></Badge>}>
            {!showCustomEmployeeCount && (
              <Form.Item name="employeeCountOptions" style={{marginBottom:0}} label="Employee Count">
                <Select
                  placeholder="Select Employee Count"
                  mode="multiple"
                  showSearch
                  optionFilterProp="children"
                >
                  {employeeCountOptions.options.map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            {showCustomEmployeeCount && (
              <Form.Item name="customEmployeeCount" style={{marginBottom:0}} label="Custom Employee Count Range">
                <Input.Group compact>
                  <Input style={{ width: '50%' }} placeholder="Min" onChange={handleMinEmployeeCountChange} />
                  <Input style={{ width: '50%' }} placeholder="Max" onChange={handleMaxEmployeeCountChange} />
                </Input.Group>
              </Form.Item>
            )}
            <Button type="link" onClick={toggleCustomEmployeeCount}
                    style={{ marginTop: 5, padding: 0, marginBottom:10 }}>
              {showCustomEmployeeCount ? 'Hide Custom Employee Count' : 'Show Custom Employee Count'}
            </Button>
          </Panel>
          <Panel header="Revenue" key="2"
                 extra={<Badge count={badgeCount(revenue, customRevenueArr)} offset={[10, 0]}><DollarOutlined /></Badge>}>
            {!showCustomRevenue && (
              <Form.Item name="revenueOptions" style={{marginBottom:0}} label="Revenue">
                <Select
                  placeholder="Select Revenue"
                  mode="multiple"
                  showSearch
                  optionFilterProp="children"
                >
                  {revenueOptions.options.map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            {showCustomRevenue && (
              <Form.Item name="customRevenue" style={{marginBottom:0}} label="Custom Revenue Range">
                <Input.Group compact>
                  <Input style={{ width: '50%' }} placeholder="Min" onChange={handleMinRevenueChange} />
                  <Input style={{ width: '50%' }} placeholder="Max" onChange={handleMaxRevenueChange} />
                </Input.Group>
              </Form.Item>
            )}
            <Button type="link" onClick={toggleCustomRevenue}
                    style={{ marginTop: 5, padding: 0, marginBottom:10 }}>
              {showCustomRevenue ? 'Hide Custom Revenue' : 'Show Custom Revenue'}
            </Button>
          </Panel>
          <Panel header="Founding Year" key="3"
                 extra={<Badge count={badgeCount(foundingYear, customFoundingYearArr)} offset={[10, 0]}><CalendarOutlined /></Badge>}>
            {!showCustomFoundingYear && (
              <Form.Item name="foundingYearOptions" style={{marginBottom:0}} label="Founding Year">
                <Select
                  placeholder="Select Founding Year"
                  mode="multiple"
                  showSearch
                  optionFilterProp="children"
                >
                  {Array.from({ length: new Date().getFullYear() - 1900 + 1 }, (_, i) => 1900 + i).map(year => (
                    <Option key={year} value={year}>{year}</Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            {showCustomFoundingYear && (
              <Form.Item name="customFoundingYear" style={{marginBottom:0}} label="Custom Founding Year Range">
                <Input.Group compact>
                  <Input style={{ width: '50%' }} placeholder="Min" onChange={handleMinFoundingYearChange} />
                  <Input style={{ width: '50%' }} placeholder="Max" onChange={handleMaxFoundingYearChange} />
                </Input.Group>
              </Form.Item>
            )}
            <Button type="link" onClick={toggleCustomFoundingYear}
                    style={{ marginTop: 5, padding: 0, marginBottom:10 }}>
              {showCustomFoundingYear ? 'Hide Custom Founding Year' : 'Show Custom Founding Year'}
            </Button>
          </Panel>
          <Panel header="Location" key="4"
                 extra={<Badge count={companyLocation.length} offset={[10, 0]}><GlobalOutlined /></Badge>}>
            <Form.Item name="companyLocationOptions" style={{marginBottom:5,marginTop:15}} label="Company Location">
              <Cascader
                options={regionCountryOptions}
                placeholder="Select Company Location"
                multiple
                showSearch
                maxTagCount="responsive"
              />
            </Form.Item>
            <Button type="link"
                    style={{ padding: 0,marginBottom:5 }}
                    onClick={() => setShowExcludeCompanyLocation(s => !s)}>
              {showExcludeCompanyLocation ? 'Hide Exclude Company Location' : 'Show Exclude Company Location'} 
            </Button>
            {showExcludeCompanyLocation && (
              <Form.Item name="excludeCompanyLocationOptions" label="Exclude Company Location">
                <Cascader
                  options={regionCountryOptions}
                  placeholder="Select Company Location to Exclude"
                  multiple
                  showSearch
                  maxTagCount="responsive"
                />
              </Form.Item>
            )}
          </Panel>
          <Panel header="Technology" key="5"
                 extra={<Badge count={technology.length} offset={[10, 0]}><ToolOutlined /></Badge>}>
            <Form.Item name="technologyOptions" label="Technology">
              <Cascader
                options={technologyOptions}
                placeholder="Select Technology"
                multiple
                showSearch
                maxTagCount="responsive"
              />
            </Form.Item>
          </Panel>
          <Panel header="Company name / Domain" key="6"
                 extra={<Badge count={includeCompanyOptions.length} offset={[10, 0]}><BankOutlined /></Badge>}>
            <Form.Item name="includeCompanyOptions" style={{ marginBottom: 5 }} label="Company Name">
              <Select
                mode="tags"
                placeholder="Enter company names or paste from Excel"
                tokenSeparators={[',']}
                value={includeCompanyOptions}
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedText = e.clipboardData.getData('Text');
                  const formattedCompanies = pastedText
                    .split(/[\t\n\r]+/)
                    .map((company) => company.trim())
                    .filter((company) => company.length > 0);
                  const currentValues = includeCompanyOptions || [];
                  form.setFieldsValue({
                    includeCompanyOptions: [...new Set([...currentValues, ...formattedCompanies])],
                  });
                }}
              />
            </Form.Item>
            <div style={{marginTop:5}}>
              <Upload beforeUpload={(file) => handleCSVUpload(file, 'includeCompanyOptions')} showUploadList={false}>
                <Button icon={<UploadOutlined />}>Upload CSV</Button>
              </Upload>
            </div>
            <Button type="link" style={{ padding: 0, marginBottom: 5 }} onClick={() => setShowExcludeCompany(e => !e)}>
              {showExcludeCompany ? 'Hide Exclude Company' : 'Show Exclude Company'}
            </Button>
            {showExcludeCompany && (
              <>
                <Form.Item name="excludeCompanyOptions" style={{ marginBottom: 5 }} label="Exclude Company">
                  <Select
                    mode="tags"
                    placeholder="Enter company names to exclude or paste from Excel"
                    value={excludeCompanyOptions}
                    tokenSeparators={[',']}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pastedText = e.clipboardData.getData('Text');
                      const formattedCompanies = pastedText
                        .split(/[\t\n\r]+/)
                        .map((company) => company.trim())
                        .filter((company) => company.length > 0);
                      const currentValues = excludeCompanyOptions || [];
                      form.setFieldsValue({
                        excludeCompanyOptions: [...new Set([...currentValues, ...formattedCompanies])],
                      });
                    }}
                  />
                </Form.Item>
                <div style={{marginTop:5}}>
                  <Upload beforeUpload={(file) => handleCSVUpload(file, 'excludeCompanyOptions')} showUploadList={false}>
                    <Button icon={<UploadOutlined />}>Upload CSV</Button>
                  </Upload>
                </div>
              </>
            )}
          </Panel>
          <Panel header="Company Keyword" key="7"
                 extra={<Badge count={companyKeywords.length} offset={[10, 0]}><BankOutlined /></Badge>}>
            <Form.Item name="companyKeywordOptions" style={{marginBottom:5}} label="Company Keyword">
              <Select
                mode="tags"
                placeholder="Enter company keywords and press enter"
                tokenSeparators={[',']}
                options={companyKeywordOptions.map(opt => ({ value: opt.name, label: opt.label }))}
              />
            </Form.Item>
          </Panel>
          <Panel header="Funding" key="8"
                 extra={<Badge count={funding.length} offset={[10, 0]}><FundOutlined /></Badge>}>
            <Form.Item name="fundingOptions" label="Funding">
              <Select
                placeholder="Select Funding"
                mode="multiple"
                showSearch
                optionFilterProp="children"
                options={fundingOptions.options}
              />
            </Form.Item>
          </Panel>
        </Collapse>
      </div>
      <div style={{ padding: 20, borderTop: '1px solid #f0f0f0', display: 'flex', gap: '10px' }}>
        <Form.Item style={{ marginBottom: 0, flex: 1 }}>
          <Button type="primary" htmlType="submit" block>Find company</Button>
        </Form.Item>
        <Form.Item style={{ marginBottom: 0, flex: 1 }}>
          <Button type="default" onClick={handleReset} block>Reset Filter</Button>
        </Form.Item>
      </div>
    </Form>
  );
}
